import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Linkedin, Phone, Mail, MapPin, ArrowRight, Send, MessageCircle } from 'lucide-react';
import Logo from './Logo';
import { services, brand, states, contactInfo } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await axios.post(`${API}/subscribe`, { email });
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3500);
    } catch (err) {
      // still show subscribed message; backend logs the error
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3500);
    }
  };

  return (
    <footer className="bg-[#0A2540] text-white">
      {/* CTA band */}
      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <div className="text-[#F5B800] uppercase text-xs font-bold tracking-[0.25em] mb-3">Ready to get started?</div>
            <h3 className="text-3xl md:text-4xl font-bold leading-tight tracking-tight">
              Get a free facility audit at your location.
            </h3>
            <p className="text-white/70 mt-3 max-w-2xl">
              Our senior consultants visit your premises, benchmark current operations and share a customised proposal within 72 hours.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row lg:justify-end gap-3">
            <Link
              to="/contact"
              className="btn-cta inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg font-semibold"
            >
              Request a Quote <ArrowRight size={16} />
            </Link>
            <a
              href={`https://wa.me/${brand.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white px-6 py-3.5 rounded-lg font-semibold transition-colors"
            >
              <MessageCircle size={16} /> WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Offices strip */}
      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <div className="text-[#F5B800] uppercase text-xs font-bold tracking-[0.25em] mb-2">Our Offices</div>
              <h3 className="text-2xl md:text-3xl font-bold tracking-tight">Reach us at any of our branches.</h3>
            </div>
            <p className="text-white/60 text-sm max-w-sm">Regional teams closer to your facility, ready to mobilise across Karnataka, Goa, Maharashtra, Tamil Nadu and Telangana.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { ...contactInfo.registered, tag: 'HQ · Mangalore' },
              { ...contactInfo.bangalore, tag: 'Bengaluru' },
              { ...contactInfo.mumbai, tag: 'Mumbai' },
            ].map((o, i) => {
              const hasMap = !!o.mapUrl;
              const openMap = () => window.open(o.mapUrl, '_blank', 'noopener,noreferrer');
              const onKeyDown = (e) => {
                if (hasMap && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  openMap();
                }
              };
              return (
                <div
                  key={i}
                  onClick={hasMap ? openMap : undefined}
                  onKeyDown={onKeyDown}
                  role={hasMap ? 'link' : undefined}
                  tabIndex={hasMap ? 0 : undefined}
                  className={`p-6 rounded-2xl bg-white/[0.04] border border-white/10 hover:border-[#F5B800]/40 transition-colors ${hasMap ? 'cursor-pointer group' : ''}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#F5B800] bg-[#F5B800]/10 border border-[#F5B800]/30 rounded-full px-3 py-1">
                      <MapPin size={11} /> {o.tag}
                    </div>
                    {hasMap && (
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-white/40 group-hover:text-[#F5B800] transition-colors">
                        View on map →
                      </span>
                    )}
                  </div>
                  <h4 className="text-white font-semibold mb-3 leading-tight">{o.title}</h4>
                  <p className="text-sm text-white/60 leading-relaxed mb-4">{o.address}</p>
                  <div className="space-y-2 text-sm">
                    {o.phone && (
                      <a
                        href={`tel:${o.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-2 text-white hover:text-[#F5B800] transition-colors font-semibold w-fit"
                      >
                        <Phone size={14} className="text-[#F5B800]" /> {o.phone}
                      </a>
                    )}
                    {o.email && (
                      <a
                        href={`mailto:${o.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-2 text-white/80 hover:text-[#F5B800] transition-colors w-fit underline decoration-dotted decoration-white/40 underline-offset-4"
                      >
                        <Mail size={14} className="text-[#F5B800]" /> {o.email}
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>


      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Logo variant="light" size="lg" />
          <p className="text-sm text-white/70 leading-relaxed mt-5 max-w-sm">
            Durga Facility Management Services Pvt. Ltd. is South India&rsquo;s trusted partner for integrated facility management since {brand.since}.
          </p>

          <form onSubmit={onSubscribe} className="mt-6 max-w-sm">
            <div className="text-[11px] font-semibold text-[#F5B800] uppercase tracking-[.14em] mb-2">
              Subscribe to our newsletter
            </div>
            <div className="flex gap-2">
              <input
                type="email"
                required
                placeholder="Your work email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/10 border border-white/15 rounded-lg px-4 py-2.5 text-sm placeholder:text-white/50 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-colors"
              />
              <button type="submit" className="bg-[#F5B800] hover:bg-[#E5AC00] rounded-lg px-4 flex items-center justify-center transition-colors" aria-label="Subscribe">
                <Send size={16} />
              </button>
            </div>
          </form>
          {subscribed && <div className="text-xs text-[#F5B800] mt-2">Thanks! We&rsquo;ll be in touch shortly.</div>}

          <div className="flex items-center gap-2.5 mt-6">
            {[
              {
                Icon: Linkedin,
                href: 'https://in.linkedin.com/company/durga-facility-management-services-private-limited-ixe',
                label: 'LinkedIn',
                testid: 'social-linkedin',
              },
              {
                Icon: MessageCircle,
                href: `https://wa.me/${brand.whatsapp}`,
                label: 'WhatsApp',
                testid: 'social-whatsapp',
              },
            ].map(({ Icon, href, label, testid }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                data-testid={testid}
                className="w-9 h-9 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center hover:bg-[#F5B800] hover:border-[#F5B800] transition-colors"
              >
                <Icon size={14} />
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-[0.15em]">Company</h4>
          <ul className="space-y-2.5 text-sm text-white/70">
            <li><Link to="/" className="hover:text-[#F5B800] transition-colors">Home</Link></li>
            <li><Link to="/about" className="hover:text-[#F5B800] transition-colors">About Us</Link></li>
            <li><Link to="/services" className="hover:text-[#F5B800] transition-colors">Services</Link></li>
            <li><Link to="/why-durga" className="hover:text-[#F5B800] transition-colors">Why Durga</Link></li>
            <li><Link to="/career" className="hover:text-[#F5B800] transition-colors">Careers</Link></li>
            <li><Link to="/#clients" className="hover:text-[#F5B800] transition-colors">Clients</Link></li>
            <li><Link to="/#faq" className="hover:text-[#F5B800] transition-colors">FAQs</Link></li>
            <li><Link to="/contact" className="hover:text-[#F5B800] transition-colors">Contact</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-2 hidden lg:block" aria-hidden="true" />

        <div className="lg:col-span-3">
          <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-[0.15em]">Reach Us</h4>
          <div className="space-y-3.5 text-sm text-white/70">
            <p className="flex gap-2.5">
              <MapPin size={16} className="shrink-0 mt-0.5 text-[#F5B800]" />
              <span>&ldquo;Bondala Annex&rdquo;, 1st Floor, Soorya Narayana Temple Road, Maroli, Kulshekar, Mangalore &ndash; 575005.</span>
            </p>
            <a href={`tel:${brand.phone}`} className="flex items-center gap-2.5 hover:text-[#F5B800] transition-colors">
              <Phone size={16} className="text-[#F5B800]" /> {brand.phone}
            </a>
            <a href={`mailto:${brand.email}`} className="flex items-center gap-2.5 hover:text-[#F5B800] transition-colors">
              <Mail size={16} className="text-[#F5B800]" /> {brand.email}
            </a>
            <a href={`https://wa.me/${brand.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 hover:text-[#F5B800] transition-colors">
              <MessageCircle size={16} className="text-[#F5B800]" /> Chat on WhatsApp
            </a>

            <div className="pt-3 mt-4 border-t border-white/10">
              <div className="text-xs text-white/50 uppercase tracking-widest mb-2">Serving</div>
              <div className="flex flex-wrap gap-1.5">
                {states.map((s) => (
                  <span key={s} className="text-[11px] px-2 py-0.5 rounded-md bg-white/10 border border-white/10">{s}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <div>&copy; {new Date().getFullYear()} Durga Facility Management Services Pvt. Ltd. All rights reserved.</div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-white transition-colors">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
